import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, Loader2Icon, MailIcon, RefreshCwIcon, XIcon } from 'lucide-react';
import emailjs from '@emailjs/browser';
// ─────────────────────────────────────────────
// EmailJS Configuration
// ─────────────────────────────────────────────
// 1. Sign up at https://www.emailjs.com (free tier: 200 emails/month)
// 2. Create an Email Service (Gmail, Outlook, etc.) → copy the Service ID
// 3. Create an Email Template with these variables:
//      {{to_email}}   — recipient address
//      {{otp_code}}   — the 6-digit code
//      {{from_name}}  — sender name shown in email (e.g. "OpenFPGA")
//    Example subject: "Your OpenFPGA access code: {{otp_code}}"
//    Example body:    "Your verification code is: {{otp_code}}\nThis code expires in 10 minutes."
// 4. Copy the Template ID and your Public Key (Account → API Keys)
// 5. Replace the placeholders below:
const EMAILJS_SERVICE_ID = 'service_4y95tzd';
const EMAILJS_TEMPLATE_ID = 'template_9rwxvli';
const EMAILJS_PUBLIC_KEY = 'R97Q3Pi4glvxrRw2H';
// ─────────────────────────────────────────────
// Google Form Configuration (for logging)
// ─────────────────────────────────────────────
// 1. Create a Google Form with an Email field
// 2. Get the form action URL: open a pre-filled link, submit, copy the URL
//    Format: https://docs.google.com/forms/d/e/FORM_ID/formResponse
// 3. Get the entry ID from the pre-filled URL (entry.XXXXXXXXXX)
const GOOGLE_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLScHLwn1eDUL_0FIKbQ3DeNk_zDusg6FKxw92VXqy2vSAa93JA/formResponse';
const GOOGLE_FORM_ENTRY_ID = 'entry.1792416468';
// ─────────────────────────────────────────────
function generateOtp(): string {
  return String(Math.floor(100000 + Math.random() * 900000));
}
const RESEND_COOLDOWN_SECONDS = 30;
const OTP_EXPIRY_MS = 10 * 60 * 1000; // 10 minutes
interface EmailGateProps {
  isOpen: boolean;
  onUnlock: () => void;
  onClose: () => void;
  requireOtp?: boolean;
}
// Logo mark matching TitleSlide
function LogoMark() {
  return <div className="flex justify-center mb-6" data-id="element-51">
      <div className="relative w-14 h-14" data-id="element-52">
        <div className="absolute inset-0 border-2 border-[#00d4ff] rounded-lg rotate-45 opacity-60" data-id="element-53" />
        <div className="absolute inset-2 border border-[#00d4ff] rounded-md rotate-45 opacity-40" data-id="element-54" />
        <div className="absolute inset-4 border border-[#00d4ff] rounded-sm rotate-45 opacity-20" data-id="element-55" />
        <div className="absolute inset-0 flex items-center justify-center" data-id="element-56">
          <div className="w-1.5 h-1.5 bg-[#00d4ff] rounded-full shadow-[0_0_12px_rgba(0,212,255,0.6)]" data-id="element-57" />
        </div>
      </div>
    </div>;
}
// 6-box OTP input
function OtpInput({
  value,
  onChange,
  disabled
}: {
  value: string;
  onChange: (val: string) => void;
  disabled: boolean;
}) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const handleChange = (index: number, char: string) => {
    const digit = char.replace(/\D/g, '').slice(-1);
    const chars = value.split('');
    chars[index] = digit;
    const next = chars.join('').slice(0, 6);
    onChange(next);
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      if (value[index]) {
        const chars = value.split('');
        chars[index] = '';
        onChange(chars.join(''));
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        const chars = value.split('');
        chars[index - 1] = '';
        onChange(chars.join(''));
      }
    }
  };
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted);
    const nextIndex = Math.min(pasted.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };
  return <div className="flex gap-2 justify-center" data-id="element-58">
      {Array.from({
      length: 6
    }).map((_, i) => <input key={i} ref={el => {
      inputRefs.current[i] = el;
    }} type="text" inputMode="numeric" maxLength={1} value={value[i] || ''} onChange={e => handleChange(i, e.target.value)} onKeyDown={e => handleKeyDown(i, e)} onPaste={handlePaste} disabled={disabled} autoFocus={i === 0} className={`w-11 h-13 text-center text-xl font-bold rounded-lg border transition-all
            bg-[rgba(255,255,255,0.03)] text-white
            ${value[i] ? 'border-[#00d4ff] shadow-[0_0_8px_rgba(0,212,255,0.3)]' : 'border-[rgba(255,255,255,0.1)]'}
            focus:outline-none focus:border-[#00d4ff] focus:ring-1 focus:ring-[#00d4ff]
            disabled:opacity-40 disabled:cursor-not-allowed`} style={{
      height: '52px'
    }} data-id="element-59" />)}
    </div>;
}
export function EmailGate({
  isOpen,
  onUnlock,
  onClose,
  requireOtp = true
}: EmailGateProps) {
  const [step, setStep] = useState<'email' | 'verify'>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(''); // generated code
  const [otpExpiry, setOtpExpiry] = useState<number>(0);
  const [enteredOtp, setEnteredOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  // Countdown timer for resend
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown(c => Math.max(0, c - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);
  const sendOtp = async (targetEmail: string): Promise<string> => {
    const code = generateOtp();
    await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
      to_email: targetEmail,
      otp_code: code,
      from_name: 'OpenFPGA'
    }, EMAILJS_PUBLIC_KEY);
    return code;
  };
  const logToGoogleForm = (targetEmail: string) => {
    fetch(GOOGLE_FORM_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({
        [GOOGLE_FORM_ENTRY_ID]: targetEmail
      })
    }).catch(() => {});
  };
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    // Direct mode: skip OTP, just log and unlock
    if (!requireOtp) {
      setLoading(true);
      logToGoogleForm(email);
      localStorage.setItem('openfpga_email_unlocked', 'true');
      setLoading(false);
      onUnlock();
      return;
    }
    setLoading(true);
    try {
      const code = await sendOtp(email);
      setOtp(code);
      setOtpExpiry(Date.now() + OTP_EXPIRY_MS);
      setStep('verify');
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      console.error('EmailJS error:', err);
      setError('Failed to send code. Check your EmailJS configuration.');
    } finally {
      setLoading(false);
    }
  };
  const handleVerifySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (enteredOtp.length < 6) {
      setError('Please enter the full 6-digit code.');
      return;
    }
    if (Date.now() > otpExpiry) {
      setError('This code has expired. Please request a new one.');
      setEnteredOtp('');
      return;
    }
    if (enteredOtp !== otp) {
      setError('Incorrect code. Please try again.');
      setEnteredOtp('');
      return;
    }
    // Verified!
    logToGoogleForm(email);
    localStorage.setItem('openfpga_email_unlocked', 'true');
    onUnlock();
  };
  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError(null);
    setEnteredOtp('');
    setLoading(true);
    try {
      const code = await sendOtp(email);
      setOtp(code);
      setOtpExpiry(Date.now() + OTP_EXPIRY_MS);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setError('Failed to resend. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  return <AnimatePresence data-id="element-60">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a0e1a]/90 backdrop-blur-sm px-4" data-id="element-61">
          <motion.div key={step} initial={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="w-full max-w-md glass-card p-8 border border-[rgba(0,212,255,0.2)] shadow-[0_0_50px_rgba(0,212,255,0.1)] relative overflow-hidden" data-id="element-62">
            {/* Top glow bar */}
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#00d4ff] to-transparent opacity-60" data-id="element-63" />

            {/* Close button */}
            <button onClick={onClose} className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full text-[#8899aa] hover:text-white hover:bg-[rgba(255,255,255,0.08)] transition-all" aria-label="Close" data-id="element-64">
              <XIcon className="w-4 h-4" data-id="element-65" />
            </button>

            <LogoMark data-id="element-66" />

            {/* ── Step 1: Email ── */}
            {step === 'email' && <>
                <div className="text-center mb-7" data-id="element-67">
                  <h2 className="text-2xl font-extrabold text-white mb-1.5" data-id="element-68">
                    Email Please
                  </h2>
                  <p className="text-[#8899aa] text-sm leading-relaxed" data-id="element-69">
                    {requireOtp ? "You'll receive a code to see the OpenFPGA deck." : 'Enter your email to view the OpenFPGA deck.'}
                  </p>
                </div>

                <form onSubmit={handleEmailSubmit} className="space-y-4" data-id="element-70">
                  <div data-id="element-71">
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@company.com" autoFocus required className="w-full px-4 py-3 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-[#8899aa]/50 focus:outline-none focus:border-[#00d4ff] focus:ring-1 focus:ring-[#00d4ff] transition-all" data-id="element-72" />
                    {error && <p className="text-[#ff6b35] text-xs mt-2" data-id="element-73">{error}</p>}
                  </div>

                  <button type="submit" disabled={loading} className="w-full py-3 px-4 bg-[#00d4ff] hover:bg-[#00bfea] text-[#0a0e1a] font-bold rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_0_20px_rgba(0,212,255,0.25)] hover:shadow-[0_0_30px_rgba(0,212,255,0.45)]" data-id="element-74">
                    {loading ? <>
                        <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-75" />
                        <span data-id="element-76">
                          {requireOtp ? 'Sending code…' : 'Unlocking…'}
                        </span>
                      </> : <>
                        <span data-id="element-77">
                          {requireOtp ? 'Send verification code' : 'View deck'}
                        </span>
                        <ArrowRightIcon className="w-4 h-4" data-id="element-78" />
                      </>}
                  </button>
                </form>

                <p className="text-center text-[10px] text-[#8899aa]/50 mt-6" data-id="element-79">
                  We'll only use this to follow up about OpenFPGA.
                </p>
              </>}

            {/* ── Step 2: Verify OTP ── */}
            {step === 'verify' && <>
                <div className="text-center mb-7" data-id="element-80">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[rgba(0,212,255,0.1)] mb-4" data-id="element-81">
                    <MailIcon className="w-5 h-5 text-[#00d4ff]" data-id="element-82" />
                  </div>
                  <h2 className="text-2xl font-extrabold text-white mb-1.5" data-id="element-83">
                    Check your inbox
                  </h2>
                  <p className="text-[#8899aa] text-sm leading-relaxed" data-id="element-84">
                    We sent a 6-digit code to{' '}
                    <span className="text-white/80 font-medium" data-id="element-85">{email}</span>.
                    Enter it below to continue.
                  </p>
                </div>

                <form onSubmit={handleVerifySubmit} className="space-y-5" data-id="element-86">
                  <OtpInput value={enteredOtp} onChange={setEnteredOtp} disabled={loading} data-id="element-87" />

                  {error && <p className="text-[#ff6b35] text-xs text-center" data-id="element-88">
                      {error}
                    </p>}

                  <button type="submit" disabled={loading || enteredOtp.length < 6} className="w-full py-3 px-4 bg-[#00d4ff] hover:bg-[#00bfea] text-[#0a0e1a] font-bold rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_20px_rgba(0,212,255,0.25)] hover:shadow-[0_0_30px_rgba(0,212,255,0.45)]" data-id="element-89">
                    {loading ? <>
                        <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-90" />
                        <span data-id="element-91">Verifying…</span>
                      </> : <span data-id="element-92">Verify & unlock deck</span>}
                  </button>
                </form>

                <div className="flex items-center justify-between mt-5" data-id="element-93">
                  <button onClick={() => {
              setStep('email');
              setError(null);
              setEnteredOtp('');
            }} className="text-xs text-[#8899aa] hover:text-white transition-colors" data-id="element-94">
                    ← Use a different email
                  </button>

                  <button onClick={handleResend} disabled={resendCooldown > 0 || loading} className="flex items-center gap-1.5 text-xs text-[#8899aa] hover:text-[#00d4ff] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-95">
                    <RefreshCwIcon className="w-3 h-3" data-id="element-96" />
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                  </button>
                </div>
              </>}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}